import { useEffect, useRef, useState, useCallback } from 'react';
import config from './config';

const normalizeChannel = (channel) => String(channel || '').replace(/^\/+/, '').replace(/\/+$/, '');

const buildWsUrl = (channel) => {
  const normalizedChannel = normalizeChannel(channel);

  // Preferred: full base URL provided via env/config (scheme + host + optional port)
  // Examples:
  // - ws://localhost:8081
  // - wss://ws.amanhmo.com
  if (config.wsBaseUrl) {
    const base = String(config.wsBaseUrl).replace(/\/+$/, '');
    const url = `${base}/${normalizedChannel}`;
    console.log(`🔗 Building WebSocket URL from base: ${url} (base: ${config.wsBaseUrl})`);
    return url;
  }

  // Fallback: construct from current host + optional env overrides.
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsHost = config.wsHost || window.location.hostname;
  const wsPort = config.wsPort;
  const portSegment = wsPort ? `:${wsPort}` : '';
  const url = `${wsProtocol}//${wsHost}${portSegment}/${normalizedChannel}`;
  
  console.log(`🔗 Building WebSocket URL from host: ${url}`, {
    protocol: wsProtocol,
    host: wsHost,
    port: wsPort,
    channel: normalizedChannel,
    config: {
      wsBaseUrl: config.wsBaseUrl,
      wsHost: config.wsHost,
      wsPort: config.wsPort,
    },
  });

  return url;
};

/**
 * Reusable WebSocket hook for real-time updates
 * 
 * @param {string} channel - WebSocket channel/path (e.g., 'appointments', 'claims')
 * @param {object} options - Configuration options
 * @param {function} options.onMessage - Callback when message is received
 * @param {function} options.onConnect - Callback when connected
 * @param {function} options.onDisconnect - Callback when disconnected
 * @param {function} options.onError - Callback when error occurs
 * @param {object} options.filter - Filter object to send to server (e.g., { facility_id: 123 })
 * @param {boolean} options.autoReconnect - Whether to auto-reconnect (default: true)
 * @param {number} options.maxReconnectAttempts - Max reconnection attempts (default: 5)
 * @param {number} options.reconnectDelay - Initial reconnect delay in ms (default: 1000)
 * 
 * @returns {object} { connected, send, close, reconnect }
 */
export const useWebSocket = (channel, options = {}) => {
  const {
    onMessage,
    onConnect,
    onDisconnect,
    onError,
    filter = null,
    autoReconnect = true,
    maxReconnectAttempts = 5,
    reconnectDelay = 1000,
    enabled = true, // Only connect if enabled is true
  } = options;

  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const enabledRef = useRef(enabled);
  const filterRef = useRef(filter);
  const onMessageRef = useRef(onMessage);
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);
  const onErrorRef = useRef(onError);
  
  // Keep enabled ref up to date
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Keep refs updated
  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    onConnectRef.current = onConnect;
  }, [onConnect]);

  useEffect(() => {
    onDisconnectRef.current = onDisconnect;
  }, [onDisconnect]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const connect = useCallback(() => {
    if (!enabledRef.current) {
      console.log(`⏸️ WebSocket connection skipped for ${channel} (disabled)`);
      return;
    }
    
    try {
      const wsUrl = buildWsUrl(channel);
      console.log(`🔌 Attempting to connect to WebSocket: ${wsUrl}`);
      console.log(`📋 Connection config:`, { channel, enabled: enabledRef.current, autoReconnect });
      
      // Check if WebSocket is already connected or connecting
      if (wsRef.current) {
        const state = wsRef.current.readyState;
        if (state === WebSocket.OPEN) {
          console.log(`✅ WebSocket already connected to ${channel}`);
          return;
        }
        if (state === WebSocket.CONNECTING) {
          console.log(`⏳ WebSocket already connecting to ${channel}`);
          return;
        }
        // If closing or closed, close the old connection first
        console.log(`🧹 Closing existing WebSocket connection (state: ${state})`);
        wsRef.current.close();
        wsRef.current = null;
      }

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log(`✅ WebSocket successfully connected to ${channel} stream`);
        console.log(`📍 WebSocket URL: ${wsUrl}`);
        console.log(`🔄 Ready State: ${ws.readyState} (OPEN)`);
        setConnected(true);
        reconnectAttemptsRef.current = 0;

        // Send filter if provided
        if (filterRef.current) {
          console.log(`📤 Sending filter to WebSocket:`, filterRef.current);
          ws.send(JSON.stringify(filterRef.current));
        }

        if (onConnectRef.current) {
          onConnectRef.current(ws);
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (onMessageRef.current) {
            onMessageRef.current(data, event);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
          if (onErrorRef.current) {
            onErrorRef.current(error);
          }
        }
      };

      ws.onerror = (error) => {
        console.error(`❌ WebSocket error on ${channel}:`, error);
        console.error(`📍 WebSocket URL: ${wsUrl}`);
        console.error(`🔄 Ready State: ${ws.readyState}`);
        setConnected(false);
        if (onErrorRef.current) {
          onErrorRef.current(error);
        }
      };

      ws.onclose = (event) => {
        console.log(`🔌 WebSocket disconnected from ${channel}`, {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        });
        setConnected(false);

        if (onDisconnectRef.current) {
          onDisconnectRef.current();
        }

        // Attempt to reconnect with exponential backoff
        // Use enabledRef to get the latest enabled value
        if (autoReconnect && enabledRef.current && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(
            reconnectDelay * Math.pow(2, reconnectAttemptsRef.current),
            30000
          );
          reconnectAttemptsRef.current += 1;
          
          console.log(`🔄 Attempting to reconnect (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts}) in ${delay}ms`);

          reconnectTimeoutRef.current = setTimeout(() => {
            if (enabledRef.current) {
              connect();
            } else {
              console.log(`⏸️ Reconnection cancelled (enabled: false)`);
            }
          }, delay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          console.warn(`⚠️ Max WebSocket reconnection attempts reached for ${channel}`);
        } else if (!enabledRef.current) {
          console.log(`⏸️ Reconnection disabled (enabled: false)`);
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error(`❌ Error connecting to WebSocket ${channel}:`, error);
      setConnected(false);
      if (onErrorRef.current) {
        onErrorRef.current(error);
      }
    }
  }, [channel, autoReconnect, maxReconnectAttempts, reconnectDelay]);

  const send = useCallback((data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(typeof data === 'string' ? data : JSON.stringify(data));
    } else {
      console.warn(`Cannot send message: WebSocket not connected to ${channel}`);
    }
  }, [channel]);

  const close = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    setConnected(false);
  }, []);

  const reconnect = useCallback(() => {
    close();
    reconnectAttemptsRef.current = 0;
    setTimeout(() => {
      connect();
    }, reconnectDelay);
  }, [close, connect, reconnectDelay]);

  // Connect on mount if enabled
  useEffect(() => {
    console.log(`🔌 useWebSocket useEffect - enabled: ${enabled}, channel: ${channel}`);
    
    if (enabled) {
      console.log(`✅ WebSocket enabled - attempting connection to ${channel}`);
      // Call connect directly to avoid circular dependency
      if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED || wsRef.current.readyState === WebSocket.CLOSING) {
        connect();
      } else {
        console.log(`ℹ️ WebSocket already active (state: ${wsRef.current.readyState})`);
      }
    } else {
      console.log(`⏸️ WebSocket disabled - closing connection to ${channel}`);
      // Close connection if disabled
      if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
        close();
      }
    }

    // Cleanup on unmount or when enabled changes
    return () => {
      if (!enabled && wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
        console.log(`🧹 Cleaning up WebSocket connection to ${channel}`);
        close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, channel]);

  // Update filter when it changes
  useEffect(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && filter !== null) {
      send(filter);
    }
  }, [filter, send]);

  return {
    connected,
    send,
    close,
    reconnect,
  };
};

export default useWebSocket;

